import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import {
  ClipboardList,
  FilePlus,
  BarChart2,
  Brain,
  CheckCircle,
  FileText,
  ArrowRight,
  Shield,
  TrendingUp,
  Users,
  Lock,
  Sparkles,
  Zap,
} from "lucide-react";

const Divider = () => (
  <div className="w-full h-1 bg-gradient-to-r from-transparent via-[#FF4D00] to-transparent max-w-6xl mx-auto my-16 opacity-60" />
);

const HomePage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    inquiryType: "",
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [displayedText, setDisplayedText] = useState("");
  const fullText =
    "Assess and strengthen your organization's governance and business continuity capability using structured COBIT 2019 and BCM assessments.";

  useEffect(() => {
    AOS.init({ duration: 900, once: true });
  }, []);

  useEffect(() => {
    let currentIndex = 0;
    let timeoutId;

    const typeCharacter = () => {
      if (currentIndex < fullText.length) {
        setDisplayedText(fullText.substring(0, currentIndex + 1));
        currentIndex++;
        timeoutId = setTimeout(typeCharacter, 30);
      }
    };

    timeoutId = setTimeout(typeCharacter, 500);

    return () => clearTimeout(timeoutId);
  }, []);

  const handleQuickFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuickFormSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    setTimeout(() => {
      navigate("/contact", { state: formData });
    }, 800);
  };

  const features = [
    {
      title: "Structured Assessments",
      icon: <ClipboardList className="w-12 h-12 text-[#FF4D00]" />,
      description:
        "Conduct structured COBIT 2019 and BCM assessments with clear sections, guided flow, and focused evaluation areas.",
      color: "from-blue-50 to-blue-100",
    },
    {
      title: "Evidence & Document Upload",
      icon: <FilePlus className="w-12 h-12 text-[#FF4D00]" />,
      description:
        "Attach supporting documents and justifications to strengthen assessment quality and support review readiness.",
      color: "from-green-50 to-green-100",
    },
    {
      title: "Auto-Generated Interim Reports",
      icon: <BarChart2 className="w-12 h-12 text-[#FF4D00]" />,
      description:
        "Generate interim reports instantly with assessment summaries, key findings, and visual scoring insights.",
      color: "from-purple-50 to-purple-100",
    },
    {
      title: "Automated Capability Scoring",
      icon: <Brain className="w-12 h-12 text-[#FF4D00]" />,
      description:
        "Automatically calculate capability or readiness levels aligned with COBIT 2019 and BCM assessment models.",
      color: "from-yellow-50 to-yellow-100",
    },
    {
      title: "Assessment Progress Tracking",
      icon: <CheckCircle className="w-12 h-12 text-[#FF4D00]" />,
      description:
        "Monitor completion progress clearly and identify areas that still need attention before submission.",
      color: "from-pink-50 to-pink-100",
    },
    {
      title: "PDF Export",
      icon: <FileText className="w-12 h-12 text-[#FF4D00]" />,
      description:
        "Export assessment outputs as professional PDF documents for internal review and stakeholder communication.",
      color: "from-orange-50 to-orange-100",
    },
  ];

  const benefits = [
    {
      icon: <Shield className="text-[#FF4D00]" size={32} />,
      title: "Stronger Risk Visibility",
      description:
        "Identify governance and continuity gaps early to support better decision-making and risk management.",
    },
    {
      icon: <TrendingUp className="text-[#FF4D00]" size={32} />,
      title: "Better Operational Improvement",
      description:
        "Use structured insights to improve governance, continuity practices, and overall organizational performance.",
    },
    {
      icon: <Users className="text-[#FF4D00]" size={32} />,
      title: "Stakeholder Alignment",
      description:
        "Support collaboration between IT, risk, audit, and business stakeholders through shared assessment visibility.",
    },
    {
      icon: <Lock className="text-[#FF4D00]" size={32} />,
      title: "Evidence-Backed Assessments",
      description:
        "Support findings with documents and structured responses for stronger internal review and accountability.",
    },
  ];

  const frameworks = [
    {
      name: "COBIT 2019",
      description: "Governance and Management Process Assessment Framework",
      processes: "40 Governance and Management Processes",
      icon: "🏛️",
      color: "from-blue-600 to-blue-700",
      details:
        "Assess individual COBIT governance and management processes, identify capability gaps, and strengthen alignment with enterprise strategy.",
    },
    {
      name: "BCM",
      description: "Business Continuity Assessment Framework",
      domains: "6 Assessment Phases",
      icon: "🔄",
      color: "from-green-600 to-green-700",
      details:
        "Evaluate continuity readiness, identify resilience gaps, and improve preparedness for disruption and recovery.",
    },
  ];

  const stats = [
    { number: "15+", label: "Assessments Completed" },
    { number: "98%", label: "User Satisfaction" },
    { number: "24/7", label: "Support Availability" },
    { number: "ISO", label: "Certified Process" },
  ];

  const howItWorks = [
    {
      number: "1",
      title: "Request Access",
      description: "Submit a request to begin your governance or business continuity assessment.",
      icon: Zap,
    },
    {
      number: "2",
      title: "Account Setup",
      description: "Our team prepares your access and provides the credentials needed to get started.",
      icon: ClipboardList,
    },
    {
      number: "3",
      title: "Complete Assessment",
      description: "Answer framework questions and upload supporting evidence throughout the assessment process.",
      icon: FilePlus,
    },
    {
      number: "4",
      title: "Review Interim Reports",
      description: "Review scores, findings, recommendations, and export your interim report for stakeholder review.",
      icon: BarChart2,
    },
  ];

  return (
    <div className="min-h-screen bg-white text-gray-800 font-sans overflow-hidden">
      <Helmet>
        <title>Governance and Business Continuity Assessment Tool</title>
        <meta
          name="description"
          content="Assess governance and business continuity capability using COBIT 2019 and BCM frameworks, with evidence upload, scoring, and interim reporting."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      {/* ===== HERO SECTION ===== */}
      <section
        className="relative h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 overflow-hidden text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url('/images/hero-bg.jpg')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-tr from-[#FF4D00]/30 via-transparent to-[#FF4D00]/40 animate-pulse pointer-events-none"></div>

        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#FF4D00] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
          <div
            className="absolute top-40 right-10 w-72 h-72 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"
            style={{ animationDelay: "2s" }}
          ></div>
          <div
            className="absolute -bottom-8 left-20 w-72 h-72 bg-orange-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"
            style={{ animationDelay: "4s" }}
          ></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto text-center w-full">
          <div data-aos="fade-down" className="mb-4 inline-block">
            <span className="inline-block px-4 py-2 bg-white/20 text-white rounded-full text-sm font-semibold flex items-center gap-2 backdrop-blur-sm">
              <Sparkles size={16} /> Governance and continuity assessments made clear
            </span>
          </div>

          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight drop-shadow-lg"
            data-aos="fade-down"
            data-aos-delay="100"
          >
            Assess Your{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-300 to-yellow-200">
              Governance and Business Continuity
            </span>{" "}
            Capability
          </h1>

          <p
            className="text-lg sm:text-xl text-white/90 mb-6 max-w-3xl mx-auto leading-relaxed drop-shadow-sm h-24 flex items-center justify-center"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            <span className="inline-block">
              {displayedText}
              <span
                className={`inline-block w-0.5 h-6 sm:h-7 ml-1 bg-white/90 ${
                  displayedText.length < fullText.length ? "animate-pulse" : ""
                }`}
              ></span>
            </span>
          </p>

          <div
            className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl mx-auto mb-8 text-sm sm:text-base"
            data-aos="fade-up"
            data-aos-delay="250"
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 border border-white/20">
              Structured COBIT 2019 and BCM assessments
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 border border-white/20">
              Evidence-backed evaluation and scoring
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 border border-white/20">
              Exportable interim reports and insights
            </div>
          </div>

          <div
            className="flex flex-col sm:flex-row gap-4 justify-center mb-8"
            data-aos="fade-up"
            data-aos-delay="300"
          >
            <button
              onClick={() => navigate("/login")}
              className="group relative inline-flex items-center justify-center px-6 sm:px-8 py-3 text-base sm:text-lg font-semibold text-white bg-gradient-to-r from-[#FF4D00] to-orange-600 rounded-xl shadow-xl hover:shadow-2xl hover:scale-105 transition duration-300 overflow-hidden backdrop-blur-sm"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-orange-600 to-[#FF4D00] opacity-0 group-hover:opacity-100 transition duration-300"></span>
              <span className="relative flex items-center gap-2">
                Start Assessment
                <ArrowRight size={18} />
              </span>
            </button>

            <button
              onClick={() => navigate("/about")}
              className="px-6 sm:px-8 py-3 text-base sm:text-lg font-semibold text-white bg-white/20 border-2 border-white rounded-xl hover:bg-white/30 hover:scale-105 transition duration-300 shadow-lg backdrop-blur-sm"
            >
              Learn More
            </button>
          </div>

          <div
            className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 pt-6 border-t border-white/30 backdrop-blur-sm"
            data-aos="fade-up"
            data-aos-delay="500"
          >
            {[
              { number: "20+", label: "Organizations" },
              { number: "2", label: "Frameworks" },
              { number: "16+", label: "Assessment Areas" },
              { number: "1000+", label: "Questions" },
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-orange-300">{stat.number}</p>
                <p className="text-white/80 text-xs sm:text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      {/* ===== FRAMEWORKS SECTION ===== */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4" data-aos="fade-up">
            Choose Your{" "}
            <span className="text-[#FF4D00]">Assessment Framework</span>
          </h2>
          <p className="text-center text-gray-600 text-lg mb-16" data-aos="fade-up" data-aos-delay="100">
            Select the framework that aligns with your organization’s governance or continuity priorities
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {frameworks.map((framework, idx) => (
              <div
                key={idx}
                className={`bg-gradient-to-br ${framework.color} text-white p-8 rounded-2xl shadow-lg hover:shadow-2xl hover:scale-105 transition duration-300 cursor-pointer group overflow-hidden relative`}
                data-aos="fade-up"
                data-aos-delay={idx * 100}
                onClick={() => navigate("/login")}
              >
                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition duration-300"></div>
                <div className="relative z-10">
                  <div className="text-6xl mb-4">{framework.icon}</div>
                  <h3 className="text-3xl font-bold mb-2">{framework.name}</h3>
                  <p className="text-white/90 mb-4">{framework.description}</p>
                  <p className="text-white/80 text-sm mb-6">{framework.domains}</p>
                  <p className="text-white/70 text-sm mb-6">{framework.details}</p>
                  <div className="inline-block bg-white text-[#FF4D00] px-6 py-2 rounded-lg font-semibold group-hover:bg-gray-100 transition">
                    Get Started →
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      {/* ===== FEATURES SECTION ===== */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4" data-aos="fade-up">
            Powerful Features
          </h2>
          <p
            className="text-center text-gray-600 text-lg mb-16"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            Everything you need for structured governance and business continuity assessments
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className={`relative p-8 rounded-2xl bg-gradient-to-br ${feature.color} border border-gray-200 hover:shadow-2xl hover:border-[#FF4D00]/30 hover:-translate-y-2 transition duration-300 group`}
                data-aos="fade-up"
                data-aos-delay={idx * 80}
              >
                <div className="mb-6 inline-block p-3 bg-white rounded-xl group-hover:bg-[#FF4D00]/10 transition">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">
                  {feature.title}
                </h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {feature.description}
                </p>
                <div className="mt-4 text-[#FF4D00] opacity-0 group-hover:opacity-100 transition">
                  <ArrowRight size={20} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      {/* ===== BENEFITS SECTION ===== */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16" data-aos="fade-up">
            Why Choose Our Platform?
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, idx) => (
              <div
                key={idx}
                className="text-center p-8 rounded-xl hover:bg-white hover:shadow-lg transition duration-300"
                data-aos="fade-up"
                data-aos-delay={idx * 100}
              >
                <div className="flex justify-center mb-4 p-4 bg-[#FF4D00]/10 rounded-full w-fit mx-auto">
                  {benefit.icon}
                </div>
                <h3 className="text-lg font-bold mb-3 text-gray-900">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      {/* ===== HOW IT WORKS ===== */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16" data-aos="fade-up">
            How It <span className="text-[#FF4D00]">Works</span>
          </h2>

          <div className="grid md:grid-cols-4 gap-6 relative">
            <div className="hidden md:block absolute top-12 left-0 right-0 h-1 bg-gradient-to-r from-[#FF4D00] to-orange-600 opacity-20"></div>

            {howItWorks.map((step, idx) => {
              const StepIcon = step.icon;
              return (
                <div
                  key={idx}
                  data-aos="fade-up"
                  data-aos-delay={idx * 100}
                  className="text-center relative z-10"
                >
                  <div className="bg-white border-4 border-[#FF4D00] text-[#FF4D00] w-24 h-24 rounded-full flex items-center justify-center text-4xl font-bold mb-4 mx-auto hover:bg-[#FF4D00] hover:text-white transition duration-300 shadow-lg">
                    {step.number}
                  </div>
                  <h3 className="font-bold text-lg mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                  <div className="mt-4">
                    <StepIcon className="text-[#FF4D00] mx-auto" size={28} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Divider />

      {/* ===== QUICK CONTACT FORM ===== */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-2xl mx-auto" data-aos="fade-up">
          <div className="bg-white rounded-2xl shadow-2xl p-8 sm:p-12 border border-gray-200">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-2">Get in Touch</h2>
              <p className="text-gray-600">
                Have questions? Complete the quick form below and our team will contact you shortly.
              </p>
            </div>

            <form onSubmit={handleQuickFormSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleQuickFormChange}
                  disabled={formSubmitted}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4D00] transition disabled:bg-gray-100"
                  required
                />

                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleQuickFormChange}
                  disabled={formSubmitted}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4D00] transition disabled:bg-gray-100"
                  required
                />
              </div>

              <select
                name="inquiryType"
                value={formData.inquiryType}
                onChange={handleQuickFormChange}
                disabled={formSubmitted}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4D00] transition disabled:bg-gray-100"
                required
              >
                <option value="">Select an inquiry type</option>
                <option value="Support">Support</option>
                <option value="Request Access">Request Access</option>
                <option value="Demo">Request Demo</option>
                <option value="Feedback">Feedback</option>
                <option value="Other">Other</option>
              </select>

              <button
                type="submit"
                disabled={formSubmitted}
                className="w-full bg-gradient-to-r from-[#FF4D00] to-orange-600 text-white py-3 rounded-lg font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {formSubmitted ? (
                  <>
                    <Sparkles size={20} className="animate-spin" /> Redirecting...
                  </>
                ) : (
                  <>
                    Continue to Contact <ArrowRight size={20} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      <Divider />

      {/* ===== FINAL CTA ===== */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#FF4D00] to-orange-600 opacity-5"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10" data-aos="fade-up">
          <h2 className="text-5xl font-bold mb-6">
            Ready to Strengthen Your{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF4D00] to-orange-600">
              Governance and Continuity Capability?
            </span>
          </h2>

          <p className="text-xl text-gray-600 mb-8">
            Start your assessment today and gain structured insights to improve governance and business continuity outcomes.
          </p>

          <button
            onClick={() => navigate("/login")}
            className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-[#FF4D00] to-orange-600 text-white text-lg font-semibold rounded-xl shadow-xl hover:shadow-2xl hover:scale-105 transition duration-300"
          >
            Start Your Assessment
            <ArrowRight size={24} />
          </button>
        </div>
      </section>

      {/* ===== FOOTER STATS ===== */}
      <section className="bg-gray-900 text-white py-12 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {stats.map((item, idx) => (
            <div key={idx} data-aos="fade-up" data-aos-delay={idx * 100}>
              <p className="text-3xl font-bold text-[#FF4D00]">{item.number}</p>
              <p className="text-gray-400 mt-2">{item.label}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default HomePage;